import { ChangeDetectionStrategy, Component, computed, inject, OnInit, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { TranslocoModule, TranslocoService } from '@ngneat/transloco';
import { SharedModule } from '@shared/shared.module';
import { Confirmer, CONFIRMER } from '@shared/components/dialogs/confirm/types';
import { Notifier, NOTIFIER } from '@shared/notifier/types';
import { PlatformRegistrationKeyDto } from '@data/_generated-api/platform-api/models/platform-registration-key-dto';
import { PlatformUserGroupService } from '@data/_generated-api/platform-api/services/platform-user-group.service';
import { firstValueFrom } from 'rxjs';
import { AccountAdminApiService } from '../../services/account-admin-api.service';
import { ESegmentedToggleOption } from '@shared/components/atoms/inputs/e-segmented-toggle/e-segmented-toggle.component';
import {
  PLATFORM_MODES,
  PlatformMode,
  PlatformModeService
} from '@/app/platform/platform-core/platform-mode/platform-mode.service';

interface UserGroupOption {
  id: string;
  name: string;
}

/**
 * Beheerpaneel voor publieke registratie (EGI-38): runtime registratie-toggle
 * en beheer van eenmalige registratiecodes. Bovenaan de platformstatus (issue #51).
 */
@Component({
  selector: 'registration-settings-panel',
  imports: [FormsModule, TranslocoModule, SharedModule],
  templateUrl: './registration-settings-panel.component.html',
  styleUrl: './registration-settings-panel.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class RegistrationSettingsPanelComponent implements OnInit {
  private readonly adminApi = inject(AccountAdminApiService);
  private readonly userGroupApi = inject(PlatformUserGroupService);
  private readonly confirmer = inject<Confirmer>(CONFIRMER);
  private readonly notifier = inject<Notifier>(NOTIFIER);
  private readonly transloco = inject(TranslocoService);
  protected readonly platformMode = inject(PlatformModeService);

  protected readonly registrationEnabled = signal(false);
  protected readonly isTogglePending = signal(false);
  protected readonly registrationKeys = signal<PlatformRegistrationKeyDto[]>([]);
  protected readonly userGroups = signal<UserGroupOption[]>([]);
  protected readonly isCreatingKey = signal(false);

  protected readonly isModePending = signal(false);

  /**
   * Zonder het recht "volledige toegang" zou een beheerder zichzelf buitensluiten: in "alleen
   * volledige toegang" verdwijnt dan ook Platform Beheer, en de modus terugzetten kan niet meer.
   */
  protected readonly modeOptions = computed<ESegmentedToggleOption<PlatformMode>[]>(() =>
    PLATFORM_MODES.map(mode => ({
      value: mode,
      label: this.t(`platformMode.${mode}`),
      disabled: mode !== 'normal' && !this.platformMode.hasFullAccess()
    }))
  );

  protected newKeyUserGroupId: string | null = null;
  protected newKeyExpiresOn: string | null = null;

  ngOnInit(): void {
    void this.platformMode.load();
    this.loadRegistrationConfig();
    this.loadRegistrationKeys();
    this.loadUserGroups();
  }

  protected async onToggleRegistration(enabled: boolean): Promise<void> {
    this.isTogglePending.set(true);
    const result = await this.adminApi.setRegistrationEnabled(enabled);
    this.isTogglePending.set(false);
    result.ifSuccess(actual => {
      this.registrationEnabled.set(actual);
      this.notifier.success(this.t(actual ? 'registrationEnabled' : 'registrationDisabled'));
    });
    result.ifError(() => {
      this.notifier.error(this.t('actionFailed'));
      this.loadRegistrationConfig();
    });
  }

  protected async onModeChange(mode: PlatformMode): Promise<void> {
    if (mode === this.platformMode.mode()) {
      return;
    }
    if (mode !== 'normal') {
      const confirmed = await this.confirmer.confirm(this.t(`platformMode.confirm.${mode}`), { color: 'warning' });
      if (!confirmed) {
        return;
      }
    }
    // BELANGRIJK: finally, want een blijvend uitgeschakelde knop is precies de knop waarmee je een
    // beperkende modus moet terugzetten
    this.isModePending.set(true);
    let saved = false;
    try {
      saved = await this.platformMode.set(mode);
    } finally {
      this.isModePending.set(false);
    }
    if (saved) {
      this.notifier.success(this.t(`platformMode.changed.${mode}`));
    } else {
      this.notifier.error(this.t('actionFailed'));
    }
  }

  protected async onCreateKey(): Promise<void> {
    if (!this.newKeyUserGroupId) {
      return;
    }
    this.isCreatingKey.set(true);
    const expiresOn = this.newKeyExpiresOn ? new Date(this.newKeyExpiresOn).toISOString() : null;
    const result = await this.adminApi.createRegistrationKey(this.newKeyUserGroupId, expiresOn);
    this.isCreatingKey.set(false);
    result.ifSuccess(() => {
      this.notifier.success(this.t('keyCreated'));
      this.newKeyExpiresOn = null;
      this.loadRegistrationKeys();
    });
    result.ifError(() => this.notifier.error(this.t('actionFailed')));
  }

  protected async onRevokeKey(key: PlatformRegistrationKeyDto): Promise<void> {
    const confirmed = await this.confirmer.confirm(this.t('confirmRevokeKey'), { color: 'danger' });
    if (!confirmed) {
      return;
    }
    const result = await this.adminApi.revokeRegistrationKey(key.id);
    result.ifSuccess(() => {
      this.notifier.success(this.t('keyRevoked'));
      this.loadRegistrationKeys();
    });
    result.ifError(() => this.notifier.error(this.t('actionFailed')));
  }

  protected async onCopyCode(key: PlatformRegistrationKeyDto): Promise<void> {
    await navigator.clipboard.writeText(key.code ?? '');
    this.notifier.success(this.t('codeCopied'));
  }

  private async loadRegistrationConfig(): Promise<void> {
    const result = await this.adminApi.isRegistrationEnabled();
    result.ifSuccess(enabled => this.registrationEnabled.set(enabled));
  }

  private async loadRegistrationKeys(): Promise<void> {
    const result = await this.adminApi.getRegistrationKeys();
    result.ifSuccess(keys => this.registrationKeys.set(keys));
    result.ifError(() => this.notifier.error(this.t('loadFailed')));
  }

  private async loadUserGroups(): Promise<void> {
    const result = await firstValueFrom(this.userGroupApi.postUsergroupsQueryDatasource({ body: {} }));
    if (result.isValid) {
      const groups = (result.value?.items ?? [])
        .filter(group => !!group.id && !!group.name)
        .map(group => ({ id: group.id, name: group.name }));
      this.userGroups.set(groups);
      this.newKeyUserGroupId ??= groups[0]?.id ?? null;
    }
  }

  private t(key: string): string {
    return this.transloco.translate(`account.admin.${key}`);
  }
}
